import { StorageBufferAttribute } from 'three/webgpu';
import { assertStorageBufferBudget } from './bindingBudget';
import {
  CONTACT_RECORD_VEC4S,
  contactRecordHelpers,
} from './contactRecord';
import { qrot, worldInvInertia } from './quatUtils';
import { localId, storage, uniform, wgslFn, workgroupId } from './tslCompat';

const WORKGROUP_SIZE = 64;
// impactState stores the epoch in f32. Keep it inside the exact-integer range
// so long-running simulations cannot silently stop advancing the epoch.
const MAX_EXACT_F32_EPOCH = 0x00ff_ffff;

/**
 * Optional coefficient-of-restitution velocity pass for AVBD.
 *
 * AVBD itself is position based and does not prescribe restitution. Call
 * initializeImpacts() immediately before AVBD velocity finalization, then call
 * solve() after finalization. The pass records the incoming contact velocity and
 * performs projected, accumulated normal-impulse Jacobi iterations afterwards.
 */
export class RestitutionStage {
  private readonly initializeImpactsKernel: any;
  private readonly solveContactsKernel: any;
  private readonly gatherLinearVelocityKernel: any;
  private readonly gatherAngularVelocityKernel: any;
  private readonly commitVelocitiesKernel: any;

  private readonly impactStateAttr: StorageBufferAttribute;
  private readonly velocityScratchAttr: StorageBufferAttribute;
  private readonly maxBodies: number;
  private readonly maxPairContacts: number;

  private epoch = 0;

  constructor(
    pairContacts: StorageBufferAttribute,
    pairActivity: StorageBufferAttribute,
    pairBodyContactCounts: StorageBufferAttribute,
    pairBodyContactIndices: StorageBufferAttribute,
    positions: StorageBufferAttribute,
    quaternions: StorageBufferAttribute,
    velocities: StorageBufferAttribute,
    angularVelocities: StorageBufferAttribute,
    inverseInertia: StorageBufferAttribute,
    maxBodies: number,
    maxPairContacts: number,
    maxPairContactsPerBody: number,
    pairActiveContactsOffset: number,
    pairActivityWordCount: number,
  ) {
    this.maxBodies = Math.max(0, Math.floor(maxBodies));
    this.maxPairContacts = Math.max(0, Math.floor(maxPairContacts));

    const boundedMaxBodies = this.maxBodies;
    const boundedMaxPairContacts = this.maxPairContacts;
    const boundedMaxPairContactsPerBody = Math.max(0, Math.floor(maxPairContactsPerBody));
    const boundedPairActivityWordCount = Math.max(0, Math.floor(pairActivityWordCount));
    const boundedPairActiveContactsOffset = Math.max(0, Math.floor(pairActiveContactsOffset));

    // Per contact: target normal velocity, accumulated lambda, Jacobi delta
    // lambda, and the initialization epoch stored as an exact small f32.
    this.impactStateAttr = new StorageBufferAttribute(
      new Float32Array(Math.max(1, boundedMaxPairContacts) * 4),
      4,
    );
    this.impactStateAttr.name = 'AVBD Restitution Impact State';

    // Two vec4 values per body: next linear and next angular velocity.
    this.velocityScratchAttr = new StorageBufferAttribute(
      new Float32Array(Math.max(1, boundedMaxBodies) * 2 * 4),
      4,
    );
    this.velocityScratchAttr.name = 'AVBD Restitution Velocity Scratch';

    const initializeImpactsShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read_write>,
        activeDispatchBound: u32,
        bodyCount: u32,
        restitution: f32,
        threshold: f32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${boundedPairActiveContactsOffset}u],
          activeDispatchBound,
        );
        if (gid >= activeCount) { return; }

        let p = pairActivity[${boundedPairActiveContactsOffset}u + gid + 1u];
        if (p >= ${boundedMaxPairContacts}u) { return; }

        let contactMeta = loadContactMeta(pairContacts, p);
        let bodyA = u32(contactMeta.x);
        let bodyB = u32(contactMeta.y);
        let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
        let normalLength2 = dot(normalRaw, normalRaw);
        if (
          contactMeta.z < 0.5
          || bodyA >= bodyCount
          || bodyB >= bodyCount
          || normalLength2 <= 1e-12
        ) {
          return;
        }

        let normal = normalRaw * inverseSqrt(normalLength2);
        let qA = normalize(quaternions[bodyA]);
        let qB = normalize(quaternions[bodyB]);
        let armA = qrot(qA, loadContactArmA(pairContacts, p).xyz);
        let armB = qrot(qB, loadContactArmB(pairContacts, p).xyz);
        let pointVelocityA = velocities[bodyA].xyz
          + cross(angularVelocities[bodyA].xyz, armA);
        let pointVelocityB = velocities[bodyB].xyz
          + cross(angularVelocities[bodyB].xyz, armB);
        let vnMinus = dot(normal, pointVelocityB - pointVelocityA);
        let targetVelocity = select(0.0, max(-restitution * vnMinus, 0.0), vnMinus < -threshold);

        impactState[p] = vec4f(targetVelocity, 0.0, 0.0, f32(epoch));
      }
    `, [qrot, contactRecordHelpers]);

    this.initializeImpactsKernel = initializeImpactsShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairActivity: storage(
        pairActivity,
        'uint',
        boundedPairActivityWordCount,
      ).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(this.impactStateAttr, 'vec4f', Math.max(1, boundedMaxPairContacts)),
      activeDispatchBound: uniform(0),
      bodyCount: uniform(0),
      restitution: uniform(0),
      threshold: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Initialize Restitution Impacts');
    assertStorageBufferBudget('AVBD Initialize Restitution Impacts', 6);

    const solveContactsShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read_write>,
        activeDispatchBound: u32,
        bodyCount: u32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${boundedPairActiveContactsOffset}u],
          activeDispatchBound,
        );
        if (gid >= activeCount) { return; }

        let p = pairActivity[${boundedPairActiveContactsOffset}u + gid + 1u];
        if (p >= ${boundedMaxPairContacts}u) { return; }

        var state = impactState[p];
        if (u32(state.w + 0.5) != epoch) { return; }

        let contactMeta = loadContactMeta(pairContacts, p);
        let bodyA = u32(contactMeta.x);
        let bodyB = u32(contactMeta.y);
        let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
        let normalLength2 = dot(normalRaw, normalRaw);
        if (
          contactMeta.z < 0.5
          || bodyA >= bodyCount
          || bodyB >= bodyCount
          || normalLength2 <= 1e-12
        ) {
          state.z = 0.0;
          impactState[p] = state;
          return;
        }

        let normal = normalRaw * inverseSqrt(normalLength2);
        let qA = normalize(quaternions[bodyA]);
        let qB = normalize(quaternions[bodyB]);
        let armA = qrot(qA, loadContactArmA(pairContacts, p).xyz);
        let armB = qrot(qB, loadContactArmB(pairContacts, p).xyz);
        let angularJacobianA = -cross(armA, normal);
        let angularJacobianB = cross(armB, normal);

        let invMassA = max(positions[bodyA].w, 0.0);
        let invMassB = max(positions[bodyB].w, 0.0);
        let localInvInertiaA = select(
          vec3f(0.0),
          max(inverseInertia[bodyA].xyz, vec3f(0.0)),
          invMassA > 0.0,
        );
        let localInvInertiaB = select(
          vec3f(0.0),
          max(inverseInertia[bodyB].xyz, vec3f(0.0)),
          invMassB > 0.0,
        );
        let worldInvInertiaA = worldInvInertia(qA, localInvInertiaA);
        let worldInvInertiaB = worldInvInertia(qB, localInvInertiaB);
        let effectiveMass = invMassA + invMassB
          + dot(angularJacobianA, worldInvInertiaA * angularJacobianA)
          + dot(angularJacobianB, worldInvInertiaB * angularJacobianB);

        if (effectiveMass <= 1e-12) {
          state.z = 0.0;
          impactState[p] = state;
          return;
        }

        let pointVelocityA = velocities[bodyA].xyz
          + cross(angularVelocities[bodyA].xyz, armA);
        let pointVelocityB = velocities[bodyB].xyz
          + cross(angularVelocities[bodyB].xyz, armB);
        let vn = dot(normal, pointVelocityB - pointVelocityA);

        // Project the accumulated impulse, not the per-iteration increment.
        // lambda stays nonnegative; deltaLambda may be negative as Jacobi
        // revises an impulse applied in an earlier iteration.
        let oldLambda = max(state.y, 0.0);
        let newLambda = max(oldLambda + (state.x - vn) / effectiveMass, 0.0);
        state.y = newLambda;
        state.z = newLambda - oldLambda;
        impactState[p] = state;
      }
    `, [qrot, worldInvInertia, contactRecordHelpers]);

    this.solveContactsKernel = solveContactsShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairActivity: storage(
        pairActivity,
        'uint',
        boundedPairActivityWordCount,
      ).toReadOnly(),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(this.impactStateAttr, 'vec4f', Math.max(1, boundedMaxPairContacts)),
      activeDispatchBound: uniform(0),
      bodyCount: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Solve Restitution Contacts');
    assertStorageBufferBudget('AVBD Solve Restitution Contacts', 8);

    const gatherLinearVelocityShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        pairBodyContactIndices: ptr<storage, array<u32>, read>,
        positions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read>,
        velocityScratch: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }

        let invMass = max(positions[body].w, 0.0);
        var totalImpulse = vec3f(0.0);
        let contactCount = min(
          pairBodyContactCounts[body],
          ${boundedMaxPairContactsPerBody}u,
        );

        for (var slot = 0u; slot < ${boundedMaxPairContactsPerBody}u; slot++) {
          if (slot >= contactCount) { break; }
          let p = pairBodyContactIndices[body * ${boundedMaxPairContactsPerBody}u + slot];
          if (p >= ${boundedMaxPairContacts}u) { continue; }

          let state = impactState[p];
          if (u32(state.w + 0.5) != epoch || state.z == 0.0) { continue; }

          let contactMeta = loadContactMeta(pairContacts, p);
          let bodyA = u32(contactMeta.x);
          let bodyB = u32(contactMeta.y);
          if (contactMeta.z < 0.5 || (body != bodyA && body != bodyB)) { continue; }

          let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
          let normalLength2 = dot(normalRaw, normalRaw);
          if (normalLength2 <= 1e-12) { continue; }
          let normal = normalRaw * inverseSqrt(normalLength2);
          let sign = select(1.0, -1.0, body == bodyA);
          totalImpulse += sign * normal * state.z;
        }

        velocityScratch[body * 2u] = vec4f(
          velocities[body].xyz + invMass * totalImpulse,
          0.0,
        );
      }
    `, [contactRecordHelpers]);

    this.gatherLinearVelocityKernel = gatherLinearVelocityShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairBodyContactCounts: storage(
        pairBodyContactCounts,
        'uint',
        boundedMaxBodies,
      ).toReadOnly(),
      pairBodyContactIndices: storage(
        pairBodyContactIndices,
        'uint',
        boundedMaxBodies * boundedMaxPairContactsPerBody,
      ).toReadOnly(),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(
        this.impactStateAttr,
        'vec4f',
        Math.max(1, boundedMaxPairContacts),
      ).toReadOnly(),
      velocityScratch: storage(this.velocityScratchAttr, 'vec4f', Math.max(1, boundedMaxBodies) * 2),
      bodyCount: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Gather Restitution Linear Velocity');
    assertStorageBufferBudget('AVBD Gather Restitution Linear Velocity', 7);

    const gatherAngularVelocityShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        pairBodyContactIndices: ptr<storage, array<u32>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        impactState: ptr<storage, array<vec4f>, read>,
        velocityScratch: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        epoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }

        let q = normalize(quaternions[body]);
        var totalTorqueImpulse = vec3f(0.0);
        let contactCount = min(
          pairBodyContactCounts[body],
          ${boundedMaxPairContactsPerBody}u,
        );

        for (var slot = 0u; slot < ${boundedMaxPairContactsPerBody}u; slot++) {
          if (slot >= contactCount) { break; }
          let p = pairBodyContactIndices[body * ${boundedMaxPairContactsPerBody}u + slot];
          if (p >= ${boundedMaxPairContacts}u) { continue; }

          let state = impactState[p];
          if (u32(state.w + 0.5) != epoch || state.z == 0.0) { continue; }

          let contactMeta = loadContactMeta(pairContacts, p);
          let bodyA = u32(contactMeta.x);
          let bodyB = u32(contactMeta.y);
          if (contactMeta.z < 0.5 || (body != bodyA && body != bodyB)) { continue; }

          let normalRaw = loadContactNormalPen(pairContacts, p).xyz;
          let normalLength2 = dot(normalRaw, normalRaw);
          if (normalLength2 <= 1e-12) { continue; }
          let normal = normalRaw * inverseSqrt(normalLength2);
          let localArm = select(
            loadContactArmB(pairContacts, p).xyz,
            loadContactArmA(pairContacts, p).xyz,
            body == bodyA,
          );
          let arm = qrot(q, localArm);
          let sign = select(1.0, -1.0, body == bodyA);
          totalTorqueImpulse += sign * cross(arm, normal) * state.z;
        }

        let localInvInertia = max(inverseInertia[body].xyz, vec3f(0.0));
        let deltaAngularVelocity = worldInvInertia(q, localInvInertia) * totalTorqueImpulse;
        velocityScratch[body * 2u + 1u] = vec4f(
          angularVelocities[body].xyz + deltaAngularVelocity,
          0.0,
        );
      }
    `, [qrot, worldInvInertia, contactRecordHelpers]);

    this.gatherAngularVelocityKernel = gatherAngularVelocityShader({
      pairContacts: storage(
        pairContacts,
        'vec4f',
        boundedMaxPairContacts * CONTACT_RECORD_VEC4S,
      ),
      pairBodyContactCounts: storage(
        pairBodyContactCounts,
        'uint',
        boundedMaxBodies,
      ).toReadOnly(),
      pairBodyContactIndices: storage(
        pairBodyContactIndices,
        'uint',
        boundedMaxBodies * boundedMaxPairContactsPerBody,
      ).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      impactState: storage(
        this.impactStateAttr,
        'vec4f',
        Math.max(1, boundedMaxPairContacts),
      ).toReadOnly(),
      velocityScratch: storage(this.velocityScratchAttr, 'vec4f', Math.max(1, boundedMaxBodies) * 2),
      bodyCount: uniform(0),
      epoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Gather Restitution Angular Velocity');
    assertStorageBufferBudget('AVBD Gather Restitution Angular Velocity', 8);

    const commitVelocitiesShader = wgslFn(/* wgsl */`
      fn compute(
        velocityScratch: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (body >= bodyCount) { return; }
        velocities[body] = velocityScratch[body * 2u];
        angularVelocities[body] = velocityScratch[body * 2u + 1u];
      }
    `);

    this.commitVelocitiesKernel = commitVelocitiesShader({
      velocityScratch: storage(
        this.velocityScratchAttr,
        'vec4f',
        Math.max(1, boundedMaxBodies) * 2,
      ).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Commit Restitution Velocities');
    assertStorageBufferBudget('AVBD Commit Restitution Velocities', 3);
  }

  initializeImpacts(
    renderer: any,
    activeDispatchBound: number,
    bodyCount: number,
    restitution: number,
    threshold: number,
  ): void {
    const boundedActiveDispatch = Math.min(
      this.maxPairContacts,
      Math.max(0, Math.floor(activeDispatchBound)),
    );
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    const boundedRestitution = Math.min(1.0, Math.max(0.0, restitution));
    const boundedThreshold = Math.max(0.0, threshold);

    this.epoch = this.epoch >= MAX_EXACT_F32_EPOCH ? 1 : this.epoch + 1;

    if (boundedActiveDispatch === 0 || boundedBodyCount === 0) return;

    const parameters = this.initializeImpactsKernel.computeNode.parameters;
    parameters.activeDispatchBound.value = boundedActiveDispatch;
    parameters.bodyCount.value = boundedBodyCount;
    parameters.restitution.value = boundedRestitution;
    parameters.threshold.value = boundedThreshold;
    parameters.epoch.value = this.epoch;

    renderer.compute(this.initializeImpactsKernel, [
      Math.ceil(boundedActiveDispatch / WORKGROUP_SIZE),
      1,
      1,
    ]);
  }

  solve(
    renderer: any,
    activeDispatchBound: number,
    bodyCount: number,
    iterations: number,
  ): void {
    const boundedActiveDispatch = Math.min(
      this.maxPairContacts,
      Math.max(0, Math.floor(activeDispatchBound)),
    );
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    const boundedIterations = Number.isFinite(iterations)
      ? Math.max(0, Math.floor(iterations))
      : 0;

    if (
      this.epoch === 0
      || boundedActiveDispatch === 0
      || boundedBodyCount === 0
      || boundedIterations === 0
    ) {
      return;
    }

    const contactParameters = this.solveContactsKernel.computeNode.parameters;
    contactParameters.activeDispatchBound.value = boundedActiveDispatch;
    contactParameters.bodyCount.value = boundedBodyCount;
    contactParameters.epoch.value = this.epoch;

    const linearParameters = this.gatherLinearVelocityKernel.computeNode.parameters;
    linearParameters.bodyCount.value = boundedBodyCount;
    linearParameters.epoch.value = this.epoch;

    const angularParameters = this.gatherAngularVelocityKernel.computeNode.parameters;
    angularParameters.bodyCount.value = boundedBodyCount;
    angularParameters.epoch.value = this.epoch;

    this.commitVelocitiesKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;

    const roundKernels: any[] = [];
    for (let iteration = 0; iteration < boundedIterations; iteration++) {
      roundKernels.push(
        this.solveContactsKernel,
        this.gatherLinearVelocityKernel,
        this.gatherAngularVelocityKernel,
        this.commitVelocitiesKernel,
      );
    }

    const dispatchWorkgroups = Math.ceil(
      Math.max(boundedActiveDispatch, boundedBodyCount) / WORKGROUP_SIZE,
    );
    renderer.compute(roundKernels, [dispatchWorkgroups, 1, 1]);
  }

  async readActiveDebugStates(renderer: any): Promise<Array<{
    contact: number;
    target: number;
    lambda: number;
    deltaLambda: number;
    storedEpoch: number;
  }> | null> {
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function' || this.epoch === 0) {
      return null;
    }
    const raw = await renderer.getArrayBufferAsync(this.impactStateAttr);
    const values = new Float32Array(raw);
    const result: Array<{ contact: number; target: number; lambda: number; deltaLambda: number; storedEpoch: number }> = [];
    for (let contact = 0; contact < this.maxPairContacts; contact++) {
      const base = contact * 4;
      const storedEpoch = values[base + 3] ?? 0;
      if (
        storedEpoch === 0
        && (values[base] ?? 0) === 0
        && (values[base + 1] ?? 0) === 0
        && (values[base + 2] ?? 0) === 0
      ) continue;
      result.push({
        contact,
        target: values[base] ?? 0,
        lambda: values[base + 1] ?? 0,
        deltaLambda: values[base + 2] ?? 0,
        storedEpoch,
      });
    }
    return result;
  }
}
